/**
 * Renders one Jñāna Kośa article from its block content (B-034-KOSA). The markup is
 * the markup the articles had as hand-written JSX, class for class, so moving them
 * into data changed no pixel. Every class is written out in full here so Tailwind
 * sees it.
 */

import React from 'react';
import { Article, Block, Cell, InlineNode, parseInline } from './kosa';

const H3 = 'text-base font-semibold text-brand dark:text-brand-dark mb-2';
const CALLOUT = 'bg-amber-50/60 dark:bg-amber-900/15 rounded-lg p-4 border border-amber-200 dark:border-amber-800';
const SPACE = { 1: 'mt-1', 2: 'mt-2', 3: 'mt-3' } as const;
const P_STYLE = {
  note: 'text-[13px] text-muted-foreground',
  verse: 'font-fraunces italic',
  label: 'text-[13px] font-medium text-brand dark:text-brand-dark mb-2',
  small: 'text-[13px]',
} as const;
const MARKER = { disc: 'list-disc', decimal: 'list-decimal' } as const;
const INDENT = { 4: 'pl-4', 5: 'pl-5' } as const;
const GAP = { '1': 'space-y-1', '1.5': 'space-y-1.5', '2': 'space-y-2' } as const;
const ROW_TONE = {
  amber: 'border-b bg-amber-50/50 dark:bg-amber-900/10',
  green: 'border-b bg-green-50/50 dark:bg-green-900/10',
} as const;
const CELL_TONE = {
  excellent: 'text-green-700 dark:text-green-400',
  'very-good': 'text-green-600 dark:text-green-500',
  good: 'text-yellow-600 dark:text-yellow-400',
  fair: 'text-orange-600 dark:text-orange-400',
} as const;
const SPAN_TONE = {
  tithi: 'text-purple-600 dark:text-purple-400',
  nakshatra: 'text-blue-600 dark:text-blue-400',
  caution: 'text-orange-600 dark:text-orange-400',
  favourable: 'text-green-600 dark:text-green-400',
} as const;

const classes = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ') || undefined;

function nodes(list: InlineNode[]): React.ReactNode[] {
  return list.map((n, k) => {
    switch (n.t) {
      case 'text': return n.v;
      case 'b': return <strong key={k}>{nodes(n.c)}</strong>;
      case 'i': return <em key={k}>{nodes(n.c)}</em>;
      case 'code': return <code key={k}>{n.v}</code>;
      case 'link':
        if (n.href.startsWith('kosa:')) {
          return <a key={k} href={`/learn/${n.href.slice(5)}`} className="text-brand dark:text-brand-dark hover:underline">{nodes(n.c)}</a>;
        }
        return (
          <a key={k} href={n.href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
            {nodes(n.c)}
          </a>
        );
      case 'span': return <span key={k} className={SPAN_TONE[n.tone]}>{nodes(n.c)}</span>;
    }
    return null;
  });
}

const Inline: React.FC<{ text: string }> = ({ text }) => <>{nodes(parseInline(text))}</>;

function cell(c: Cell, dense: boolean | undefined, k: number) {
  const { text, medium, small, tone } = typeof c === 'string' ? { text: c } as Exclude<Cell, string> : c;
  return (
    <td key={k} className={classes(dense ? 'py-1.5 px-4' : 'py-2 px-4', medium && 'font-medium', small && 'text-[12px]', tone && CELL_TONE[tone])}>
      <Inline text={text} />
    </td>
  );
}

function block(b: Block, k: number): React.ReactNode {
  switch (b.type) {
    case 'p':
      return <p key={k} className={classes(b.space && SPACE[b.space], b.style && P_STYLE[b.style])}><Inline text={b.text} /></p>;
    case 'list': {
      const Tag = b.marker === 'decimal' ? 'ol' : 'ul';
      return (
        <Tag key={k} className={classes(b.marker !== 'none' && `${MARKER[b.marker]} ${INDENT[b.indent ?? 5]}`, b.space && SPACE[b.space], GAP[b.gap], b.small && 'text-[13px]')}>
          {b.items.map((item, n) => <li key={n}><Inline text={item} /></li>)}
        </Tag>
      );
    }
    case 'table':
      return (
        <div key={k} className={`overflow-x-auto ${SPACE[b.space]}`}>
          <table className={b.grid ? 'text-[13px] border-collapse' : 'text-[13px]'}>
            {b.head && (
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  {b.head.map((h, n) => <th key={n} className="py-2 px-4 font-medium"><Inline text={h} /></th>)}
                </tr>
              </thead>
            )}
            <tbody>
              {b.rows.map((r, n) => (
                <tr key={n} className={b.grid ? undefined : r.tone ? ROW_TONE[r.tone] : 'border-b'}>
                  {b.grid
                    ? r.cells.map((c, m) => {
                      const g = typeof c === 'string' ? { text: c } as Exclude<Cell, string> : c;
                      return <td key={m} className="border px-4 py-2 text-center" colSpan={g.colspan} rowSpan={g.rowspan}><Inline text={g.text} /></td>;
                    })
                    : r.cells.map((c, m) => cell(c, b.dense, m))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'callout':
      return (
        <div key={k} className={classes(b.tone === 'plain' ? 'rounded-lg p-4 border border-border' : CALLOUT, b.space && SPACE[b.space])}>
          {b.blocks.map(block)}
        </div>
      );
    case 'verse':
      return (
        <div key={k} className={`${CALLOUT} mt-3`}>
          <p className="font-fraunces italic"><Inline text={b.original} /></p>
          <p className="mt-1"><Inline text={b.translation} /></p>
        </div>
      );
    case 'h4':
      return <h4 key={k} className="text-sm font-semibold mb-2"><Inline text={b.text} /></h4>;
    case 'group':
      return <div key={k} className="mt-4 space-y-4">{b.blocks.map(block)}</div>;
  }
  return null;
}

const KosaArticle: React.FC<{ article: Article }> = ({ article }) => (
  <div className="space-y-6 text-[14px] leading-relaxed">
    {article.sections.map((s, n) => (
      <section key={n}>
        {s.heading !== undefined && <h3 className={H3}><Inline text={s.heading} /></h3>}
        {s.blocks.map(block)}
      </section>
    ))}
  </div>
);

export default KosaArticle;
